
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { readFormId, withForm } from '../utils/wizardForm';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';
import IrsMaintenanceBanner from "./IrsMaintenanceBanner";

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

export default function ChangeVehicleSuspension() {
  const { id: companyId } = useParams();
  const navigate = useNavigate();

  // Контекст поправки — из адреса (его кладёт BeginAmendedReturn)
  const [searchParams]     = useSearchParams();
  const submissionId       = searchParams.get('sid');
  const token              = localStorage.getItem('token');
  const prevSubmissionDate = searchParams.get('date');
  const prevFormId         = readFormId(searchParams);

  const [availableVins, setAvailableVins] = useState([]); // [{id, vin, gross_vehicle_label}, ...]
  const [pickVin, setPickVin]             = useState('');
  const [generalMonth, setGeneralMonth]   = useState('');
  const [suspendedList, setSuspendedList] = useState([]); // ["VIN1","VIN2",...]
  const [error, setError]                 = useState('');
  const [saving, setSaving]               = useState(false);
  const [loading, setLoading]             = useState(true);

  // загрузка VIN из предыдущей подачи
  useEffect(() => {
    const load = async () => {
      try {
        if (!submissionId) {
          setError('Previous submission not selected.');
          setLoading(false);
          return;
        }
        const res = await fetch(`/api/submissions/${submissionId}/vehicles/`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error('Failed to load VIN list');
        const data = await res.json();
        setAvailableVins(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setError('Unable to load vehicles for the previous submission.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [submissionId, token]);

  // исключаем уже добавленные и (опционально) уже W
  const remainingVins = useMemo(() => {
    const picked = new Set(suspendedList);
    return (availableVins || [])
      // фильтранём уже W, чтобы не предлагать их повторно (можно убрать, если не нужно)
      .filter(v => (v.gross_vehicle_label || '').toUpperCase() !== 'W')
      .filter(v => !picked.has(v.vin));
  }, [availableVins, suspendedList]);

  const handleAdd = () => {
    if (!pickVin) {
      setError('Select a VIN to mark as suspended.');
      return;
    }
    setSuspendedList(prev => [...prev, pickVin]);
    setPickVin('');
    setError('');
  };

  const handleRemove = (vin) => {
    setSuspendedList(prev => prev.filter(x => x !== vin));
  };

  const handleSave = async () => {
    if (!prevFormId || !submissionId || !prevSubmissionDate) {
      setError('Missing context: previous form or submission not found.');
      return;
    }
    if (!generalMonth) {
      setError('Select the overall amended month.');
      return;
    }
    if (!suspendedList.length) {
      setError('Add at least one VIN to suspend.');
      return;
    }

    setSaving(true);
    setError('');

    const payload = {
      form2290: parseInt(prevFormId, 10),
      prev_submission_id: submissionId,
      change_type: 'Suspension',
      prev_submission_date: prevSubmissionDate,
      amendment_month: generalMonth,
      changes: suspendedList, // ← список VIN, ставим им W на бэке
    };

    try {
      const res = await fetch('/api/submission-detail/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(t || 'Failed to save Suspended changes');
      }
      navigate(withForm(`/company/${companyId}/amended-payment-and-submission`, prevFormId, { type: 'Suspension' }));
    } catch (e) {
      console.error(e);
      setError('Unable to save Suspended changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#E4F2FF]">
        <p className="text-gray-700 text-lg">Loading VINs…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#E4F2FF]">
      <HeaderLogged />
      <main className="flex-1 py-6 px-4">
        <div className="max-w-3xl mx-auto bg-white shadow-md p-6 rounded-lg">
          <h1 className="text-2xl font-semibold text-[#0F2C5E] mb-4">
            Mark Vehicles as Suspended
          </h1>

          {/* Overall Amended Month */}
          <div className="mb-6">
            <label className="block font-medium mb-1">
              Overall Amended Month <span className="text-red-500">*</span>
            </label>
            <select
              value={generalMonth}
              onChange={e => setGeneralMonth(e.target.value)}
              className="w-1/2 border p-2 focus:outline-none focus:ring-2 focus:ring-[#0F2C5E]"
            >
              <option value="">Select Month</option>
              {MONTHS.map(m => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* Add Row */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mb-6">
            {/* VIN select */}
            <div className="sm:col-span-4">
              <label className="block text-sm font-medium mb-1">VIN</label>
              <select
                value={pickVin}
                onChange={e => setPickVin(e.target.value)}
                className="w-full border p-2 focus:outline-none focus:ring-2 focus:ring-[#0F2C5E]"
              >
                <option value="">Select VIN</option>
                {remainingVins.map(v => (
                  <option key={v.id} value={v.vin}>
                    {v.vin} {v.gross_vehicle_label ? `(${v.gross_vehicle_label})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Add button */}
            <div className="sm:col-span-1 flex items-end">
              <button
                onClick={handleAdd}
                className="w-full bg-[#0F2C5E] text-white py-2 rounded hover:bg-[#0F2C5E]/80 transition"
              >
                Add
              </button>
            </div>
          </div>

          {/* Ошибки */}
          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          {/* Таблица выбранных VIN */}
          {suspendedList.length > 0 ? (
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left px-4 py-2">VIN</th>
                    <th className="px-4 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {suspendedList.map((vin) => (
                    <tr key={vin}>
                      <td className="px-4 py-2 font-mono">{vin}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => handleRemove(vin)}
                          className="text-red-600 hover:underline text-sm"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 mb-6">No VINs added yet.</p>
          )}

          {/* Actions */}
          <div className="flex justify-between">
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition"
            >
              Back
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !suspendedList.length || !generalMonth}
              className={`px-4 py-2 rounded text-white transition ${
                saving || !suspendedList.length || !generalMonth
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#0F2C5E] hover:bg-[#0F2C5E]/80'
              }`}
            >
              {saving ? 'Saving…' : 'Next'}
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
